import { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";

const categories = ["All", "Reconciliation", "Construction", "Nonprofits", "AI & Analysis"];

const videos = [
  { id: "mrPpRV3GCn8", category: "Reconciliation", title: "How to Reconcile Your Bank in QuickBooks Online", author: "Hector Garcia, CPA", note: "The definitive walkthrough of bank reconciliation — cleared transactions, ending balances, and reports." },
  { id: "ucDOyAYvtsM", category: "Reconciliation", title: "Reconcile Bank & Credit Cards, Step by Step", author: "My Cloud Bookkeeping", note: "A modern step-by-step tutorial with troubleshooting tips for discrepancies and duplicates." },
  { id: "IUEutWm-t10", category: "Construction", title: "Construction Bookkeeping for Contractors", author: "Rapid Result Creators", note: "Simplified bookkeeping foundations built specifically for contractors and job-cost tracking." },
  { id: "JCyICc-_52I", category: "Construction", title: "QuickBooks Online for Construction", author: "Hector Garcia, CPA", note: "Setting up QuickBooks the right way for a construction business — estimates, projects, and costs." },
  { id: "O0940iTyWXw", category: "Nonprofits", title: "Fund Accounting & Nonprofit Grant Compliance", author: "The Nonprofit Digest", note: "Why tracking restricted funds correctly is the key to clean audits and grant compliance." },
  { id: "CcubIchQCV0", category: "Nonprofits", title: "Introduction to Nonprofit Accounting", author: "TechSoup", note: "A full webinar on double-entry bookkeeping and managing nonprofit books in QuickBooks." },
  { id: "FKLr3ft8ea0", category: "AI & Analysis", title: "Master Data Analysis with ChatGPT", author: "Jeff Su", note: "Practical ChatGPT tips for everyday analysis — describe, question, and set goals for any dataset in minutes." },
  { id: "CFAMh7NMqgo", category: "AI & Analysis", title: "ChatGPT for Accountants (with Use Cases)", author: "365 Financial Analyst", note: "Real use cases for AI in accounting — data extraction, Excel formulas, and financial analysis." },
  { id: "i2WxKY0LhTE", category: "AI & Analysis", title: "Build an AI-Based Accounting System with ChatGPT", author: "Nerd Enterprises", note: "Turn transaction data into summaries, health checks, and client-ready presentations with AI." },
];

function VideoCard({ v, i }) {
  const [playing, setPlaying] = useState(false);
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.76, 0, 0.24, 1] }}
      className="group flex flex-col border border-linen bg-white/60"
      data-testid={`resource-card-${v.id}`}
    >
      <div className="relative aspect-video w-full overflow-hidden bg-pine">
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${v.id}?autoplay=1&rel=0`}
            title={v.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="h-full w-full"
            data-testid={`resource-iframe-${v.id}`}
          />
        ) : (
          <button
            onClick={() => setPlaying(true)}
            className="relative h-full w-full"
            aria-label={`Play ${v.title}`}
            data-testid={`resource-play-${v.id}`}
          >
            <img
              src={`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`}
              alt={v.title}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-pine/30 transition-colors duration-300 group-hover:bg-pine/10">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-bone/90 text-forest shadow-lg transition-transform duration-300 group-hover:scale-110">
                <Play size={20} className="ml-0.5" fill="currentColor" />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="flex flex-1 flex-col p-7">
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-terra">{v.category}</p>
        <h3 className="mb-2 font-serif text-xl font-semibold leading-snug tracking-tight text-pine">{v.title}</h3>
        <p className="mb-4 flex-1 text-sm leading-relaxed text-pine/70">{v.note}</p>
        <div className="flex items-center justify-between gap-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-forest">{v.author}</p>
          <a
            href={`https://www.youtube.com/watch?v=${v.id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`resource-youtube-link-${v.id}`}
            className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.2em] text-terra underline-offset-4 transition-colors duration-300 hover:underline"
          >
            Watch on YouTube ↗
          </a>
        </div>
      </div>
    </motion.article>
  );
}

export default function Resources() {
  const [active, setActive] = useState("All");
  const shown = active === "All" ? videos : videos.filter((v) => v.category === active);
  return (
    <section id="resources" data-testid="resources-section" className="border-t border-linen bg-white/40">
      <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-terra"
        >
          Free Learning Library
        </motion.p>
        <div className="mb-14 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="max-w-2xl font-serif text-4xl font-medium leading-tight tracking-tight text-pine md:text-5xl"
            data-testid="resources-heading"
          >
            Accounting &amp; finance <span className="italic text-forest">resources</span>, curated for you
          </motion.h2>
          <p className="max-w-sm text-sm leading-relaxed text-pine/60" data-testid="resources-subtext">
            Hand-picked, free public videos on the fundamentals — from reconciling your books to
            simplified bookkeeping for construction firms and nonprofits, plus AI-powered analysis
            skills from the founder&apos;s own LinkedIn Learning path, including his certificate in
            15 ChatGPT tips for everyday data analysis.
          </p>
        </div>
        <div className="mb-12 flex flex-wrap gap-2" data-testid="resources-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`resources-filter-${c.toLowerCase()}`}
              className={`border px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                active === c
                  ? "border-forest bg-forest text-bone"
                  : "border-linen text-pine/60 hover:border-forest/40 hover:text-forest"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="resources-grid">
          {shown.map((v, i) => (
            <VideoCard key={v.id} v={v} i={i} />
          ))}
        </div>
        <p className="mt-12 max-w-2xl font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-pine/40" data-testid="resources-disclaimer">
          These are publicly available third-party videos shared for educational purposes. For advice
          specific to your books, book a consultation.
        </p>
      </div>
    </section>
  );
}
